import { Appointment } from '@interface/appointment';
import { Clinic, Professional } from '@interface/clinic';
import { InvoiceFilters } from '@interface/invoices';
import { LoginResponse } from '@interface/Login';
import { ProductType } from '@interface/product';
import { TicketSaleResponse } from '@interface/ticket';
import { Cursor } from 'app/(dashboard)/dashboard/(pages)/all-budgets/components/table/TableFunctions';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface DashboardStore {
  showRegistration: boolean;
  clinic: Clinic | undefined;
  cursors: Cursor[];
  professional: Professional | undefined;
  date: string | null;
  phoneNumber: string;
  isLoading: boolean;
  errors: Array<string>;
  userEmail: string;
  isLoadingAppointments: boolean;
  loadingAppointments: {
    [id: string]: boolean;
  };
  selectedType: ProductType;
  appointments: Appointment[];
  defaultAppointmentForComment: Appointment | undefined;
  messageNotification: string | null;
  loadingState: { [key: string]: boolean };
  userLoginResponse: LoginResponse | undefined;
  budgetsFilterText: string;
  budgetsTotalCount: number;
  budgetsFilterStatus: string;
  budgetsItemsPerPage: number;
  budgetsCurrentPage: number;
  budgetsStartDateFilter: Date | undefined;
  budgetsEndDateFilter: Date | undefined;
  budgetsIsLoading: boolean;
  invoicesFilters: InvoiceFilters;
  ticketSaleResponse: TicketSaleResponse | undefined;
  budgetProfessionalFilter: Professional | undefined;
}

interface DashboardActions {
  setClinic: (clinic: Clinic | undefined) => void;
  setCursors: (cursors: Cursor[]) => void;
  setProfessional: (professional: Professional | undefined) => void;
  setShowRegistration: (value: boolean) => void;
  setDate: (value: string | null) => void;
  setPhoneNumber: (value: string) => void;
  setIsLoading: (value: boolean) => void;
  setErrors: (value: Array<string>) => void;
  setUserEmail: (value: string) => void;
  setIsLoadingAppointments: (value: boolean) => void;
  setLoadingAppointments: (value: { [id: string]: boolean }) => void;
  setAppointments: (value: Appointment[]) => void;
  setDefaultAppointmentForComment: (value: Appointment) => void;
  setSelectedType: (value: ProductType) => void;
  setMessageNotification: (value: string | null) => void;
  setLoadingState: (value: { [key: string]: boolean }) => void;
  setUserLoginResponse: (userLoginResponse: LoginResponse | undefined) => void;
  setBudgetsFilterText: (value: string) => void;
  setBudgetsTotalCount: (value: number) => void;
  setBudgetsFilterStatus: (value: string) => void;
  setBudgetsItemsPerPage: (value: number) => void;
  setBudgetsCurrentPage: (value: number) => void;
  setBudgetsStartDateFilter: (value: Date | undefined) => void;
  setBudgetsEndDateFilter: (value: Date | undefined) => void;
  setBudgetsIsLoading: (value: boolean) => void;
  setInvoicesFilters: (value: InvoiceFilters) => void;
  setTicketSaleResponse: (value: TicketSaleResponse | undefined) => void;
  setBudgetProfessionalFilter: (value: Professional | undefined) => void;
}

export const useDashboardStore = create(
  persist<DashboardStore & DashboardActions>(
    set => ({
      clinic: undefined,
      setClinic: value => {
        set({ clinic: value });
      },
      cursors: [],
      setCursors: value => {
        set({ cursors: value });
      },
      professional: undefined,
      setProfessional: value => {
        set({ professional: value });
      },
      budgetProfessionalFilter: undefined,
      setBudgetProfessionalFilter: (value: Professional | undefined) => {
        set({ budgetProfessionalFilter: value });
      },
      showRegistration: false,
      setShowRegistration: (value: boolean) => {
        set({ showRegistration: value });
      },
      phoneNumber: '',
      setPhoneNumber: (value: string) => {
        set({ phoneNumber: value });
      },
      date: '',
      setDate: (value: string | null) => {
        set({ date: value });
      },
      isLoading: false,
      setIsLoading: (value: boolean) => {
        set({ isLoading: value });
      },
      errors: [],
      setErrors: (value: Array<string>) => {
        set({ errors: value });
      },
      userEmail: '',
      setUserEmail: (value: string) => {
        set({ userEmail: value });
      },
      isLoadingAppointments: false,
      setIsLoadingAppointments: (value: boolean) => {
        set({ isLoadingAppointments: value });
      },
      loadingAppointments: {},
      setLoadingAppointments: (value: { [id: string]: boolean }) => {
        set({ loadingAppointments: value });
      },
      appointments: [],
      setAppointments: (value: Appointment[]) => {
        set({ appointments: value });
      },
      defaultAppointmentForComment: undefined,
      setDefaultAppointmentForComment: (value: Appointment) => {
        set({ defaultAppointmentForComment: value });
      },
      selectedType: 0,
      setSelectedType: (value: ProductType) => {
        set({ selectedType: value });
      },
      messageNotification: null,
      setMessageNotification: (value: string | null) => {
        set({ messageNotification: value });
      },
      loadingState: {},
      setLoadingState: (value: { [key: string]: boolean }) => {
        set({ loadingState: value });
      },
      userLoginResponse: undefined,
      setUserLoginResponse: value => {
        set({ userLoginResponse: value });
      },
      budgetsFilterText: '',
      setBudgetsFilterText: (value: string) => {
        set({ budgetsFilterText: value });
      },
      budgetsTotalCount: 0,
      setBudgetsTotalCount: (value: number) => {
        set({ budgetsTotalCount: value });
      },
      budgetsFilterStatus: '',
      setBudgetsFilterStatus: value => {
        set({ budgetsFilterStatus: value });
      },
      budgetsItemsPerPage: 10,
      setBudgetsItemsPerPage: value => {
        set({ budgetsItemsPerPage: value });
      },
      budgetsCurrentPage: 1,
      setBudgetsCurrentPage: value => {
        set({ budgetsCurrentPage: value });
      },
      budgetsStartDateFilter: undefined,
      setBudgetsStartDateFilter: value => {
        set({ budgetsStartDateFilter: value });
      },
      budgetsEndDateFilter: undefined,
      setBudgetsEndDateFilter: value => {
        set({ budgetsEndDateFilter: value });
      },
      budgetsIsLoading: false,
      setBudgetsIsLoading: value => {
        set({ budgetsIsLoading: value });
      },
      invoicesFilters: {
        clinic: [],
        startDate: undefined,
        endDate: undefined,
      },
      setInvoicesFilters: value => {
        set({ invoicesFilters: value });
      },
      ticketSaleResponse: undefined,
      setTicketSaleResponse: value => {
        set({ ticketSaleResponse: value });
      },
    }),
    {
      name: 'dashboard-storage',
      version: Number(process.env.NEXT_PUBLIC_STORE_VERSION || '2'),
      storage: createJSONStorage(() => sessionStorage),
      migrate: persistedState => {
        const state = persistedState as DashboardStore & DashboardActions;
        state.clinic = undefined;
        state.cursors = [];
        state.professional = undefined;
        state.showRegistration = false;
        state.phoneNumber = '';
        state.date = '';
        state.isLoading = false;
        state.errors = [];
        state.userEmail = '';
        state.isLoadingAppointments = false;
        state.appointments = [];
        state.selectedType = 0;
        state.messageNotification = null;
        state.loadingState = {};
        state.userLoginResponse = undefined;
        state.budgetsFilterText = '';
        state.budgetsTotalCount = 0;
        state.budgetsFilterStatus = '';
        state.budgetsItemsPerPage = 10;
        state.budgetsCurrentPage = 1;
        state.budgetsStartDateFilter = undefined;
        state.budgetsEndDateFilter = undefined;
        state.budgetsIsLoading = false;
        state.defaultAppointmentForComment = undefined;
        state.invoicesFilters = {
          clinic: [],
          startDate: undefined,
          endDate: undefined,
        };
        state.ticketSaleResponse = undefined;
        return {
          ...state,
        };
      },
    }
  )
);
